import React, { useEffect, useState } from "react";
import { hasSupabaseConfig, supabase } from "../../lib/supabaseClient";

const OPS_SHELL_CLASS = "min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(124,58,237,0.24),transparent_28%),radial-gradient(circle_at_top_right,rgba(239,68,68,0.18),transparent_24%),linear-gradient(180deg,#fcfbff_0%,#f7f4fb_52%,#f6f1f4_100%)] text-[#111111]";
const OPS_PANEL_CLASS = "rounded-[32px] border border-black/10 bg-white/88 shadow-[0_30px_90px_rgba(17,17,17,0.08)] backdrop-blur";
const OPS_INPUT_CLASS = "w-full rounded-full border border-black/10 bg-white px-4 py-3 text-[#111111] outline-none transition placeholder:text-black/35 focus:border-[#7c3aed] focus:ring-4 focus:ring-[#a78bfa]/35";
const OPS_PRIMARY_BUTTON_CLASS = "w-full rounded-full border border-black bg-[#111111] px-4 py-3 text-sm font-semibold text-white transition hover:bg-black disabled:opacity-60";
const OPS_SECONDARY_BUTTON_CLASS = "rounded-full border border-black/10 bg-white px-4 py-3 text-sm font-medium text-[#111111] transition hover:border-black/25 hover:bg-black/[0.03] disabled:opacity-60";
const OPS_TAB_BUTTON_CLASS = "rounded-full border px-4 py-2.5 text-sm font-semibold transition";

const isLocalDevTournamentBypass =
  process.env.NODE_ENV === "development" &&
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

const TOURNAMENT_OPS_FOOTER_COPY = "Puerto Rico Esports League TM. Todos los derechos reservados.";

function formatAuthMessage(prefix, error) {
  const raw = String(error?.message || "").trim();
  const normalized = raw.toLowerCase();

  if (
    normalized.includes("rate limit")
    || normalized.includes("security purposes")
    || normalized.includes("too many requests")
    || normalized.includes("over_email_send_rate_limit")
  ) {
    return `${prefix}: Llegaste al limite temporal de intentos o correos. Espera unos minutos antes de volver a intentar. Si ya tienes cuenta, tambien puedes usar Contraseña o Recuperar contraseña.`;
  }

  return `${prefix}: ${raw || "Ocurrio un error inesperado."}`;
}

async function resolveTournamentRole(email) {
  if (!email) return null;

  const normalizedEmail = email.toLowerCase();

  try {
    const response = await fetch(`/api/tournament-ops/access-check?email=${encodeURIComponent(normalizedEmail)}`);
    if (response.ok) {
      const payload = await response.json();
      if (payload?.approved && payload?.role) {
        return {
          role: payload.role,
          source: payload.source || "tournament_ops_access_check",
        };
      }
    }
  } catch (_error) {
    // Fall through to direct client lookup when backend validation is unavailable.
  }

  if (!supabase) return null;

  const { data: staffRow, error: staffError } = await supabase
    .from("tournament_staff_users")
    .select("role,email")
    .ilike("email", normalizedEmail)
    .limit(1)
    .maybeSingle();

  if (!staffError && staffRow?.role) {
    return {
      role: staffRow.role,
      source: "tournament_staff_users",
    };
  }

  const { data: adminRow, error: adminError } = await supabase
    .from("admin_users")
    .select("role,email")
    .ilike("email", normalizedEmail)
    .limit(1)
    .maybeSingle();

  if (!adminError && adminRow?.role) {
    return {
      role: adminRow.role,
      source: "admin_users",
    };
  }

  return null;
}

function TournamentOpsFooter() {
  return (
    <footer className="mt-8 border-t border-black/10 pt-5 text-center text-xs text-black/55">
      <div className="mx-auto flex max-w-lg flex-col items-center gap-3">
        <img
          src="/tournament-ops-assets/prel-logo-v1.png"
          alt="Puerto Rico Esports League"
          className="h-10 w-auto object-contain opacity-90"
          loading="lazy"
          decoding="async"
        />
        <p>{TOURNAMENT_OPS_FOOTER_COPY}</p>
      </div>
    </footer>
  );
}

export default function TournamentOpsAccessGate({ children }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [access, setAccess] = useState(null);

  const [mode, setMode] = useState("signup");
  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function fetchApprovedAccess(email) {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) return null;

    try {
      const response = await fetch("/api/tournament-ops/access-check", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || "No se pudo validar el acceso.");
      }
      return {
        approved: Boolean(payload?.approved),
        role: payload?.role || null,
        source: payload?.source || "tournament_ops_access_check",
        authUserExists: Boolean(payload?.authUserExists),
        emailConfirmed: Boolean(payload?.emailConfirmed),
      };
    } catch (_error) {
      const fallbackRole = await resolveTournamentRole(normalizedEmail);
      return fallbackRole
        ? {
            approved: true,
            role: fallbackRole.role,
            source: fallbackRole.source,
            authUserExists: false,
            emailConfirmed: false,
          }
        : null;
    }
  }

  useEffect(() => {
    if (isLocalDevTournamentBypass) {
      setSession({
        user: {
          email: "local-tournament-admin@prel.local",
        },
      });
      setAccess({
        role: "tournament_admin",
        source: "local_dev_bypass",
      });
      setMessage("Bypass local activo para revisar Tournament Ops sin login.");
      setLoading(false);
      return;
    }

    if (!hasSupabaseConfig || !supabase) {
      setLoading(false);
      setMessage("Falta configurar Supabase para Tournament Ops.");
      return;
    }

    let mounted = true;

    async function resolveMagicLinkSession() {
      const url = new URL(window.location.href);
      const code = url.searchParams.get("code");
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const accessToken = hash.get("access_token");
      const refreshToken = hash.get("refresh_token");

      if (code) {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) throw error;
        if (data?.session) {
          window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search.replace(/([?&])code=[^&]*&?/, "$1").replace(/[?&]$/, "")}`);
          return data.session;
        }
      }

      if (accessToken && refreshToken) {
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error) throw error;
        if (data?.session) {
          window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
          return data.session;
        }
      }

      return null;
    }

    async function bootstrap() {
      let nextSession = null;
      try {
        nextSession = await resolveMagicLinkSession();
      } catch (error) {
        if (mounted) {
          setMessage(formatAuthMessage("No se pudo completar el acceso por magic link", error));
        }
      }

      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(nextSession || data?.session || null);
      setLoading(false);
    }

    bootstrap();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession || null);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function checkAccess() {
      if (isLocalDevTournamentBypass) {
        setAccess({
          role: "tournament_admin",
          source: "local_dev_bypass",
        });
        return;
      }

      if (!session?.user?.email) {
        setAccess(null);
        return;
      }

      const result = await resolveTournamentRole(session.user.email);
      if (!cancelled) {
        setAccess(result);
      }
    }

    checkAccess();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.email]);

  async function loginWithPassword(e) {
    e.preventDefault();
    setMessage("");

    if (!supabase) return;

    const email = emailInput.trim().toLowerCase();
    if (!email || !passwordInput) {
      setMessage("Completa email y contraseña.");
      return;
    }

    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password: passwordInput,
    });
    setBusy(false);

    if (error) {
      setMessage(formatAuthMessage("No se pudo iniciar sesión", error));
      return;
    }

    setMessage("Sesión iniciada.");
  }

  async function createPasswordAccess(e) {
    e.preventDefault();
    setMessage("");

    if (!supabase) return;

    const email = emailInput.trim().toLowerCase();
    if (!email || !passwordInput) {
      setMessage("Completa email y contraseña para crear el acceso.");
      return;
    }

    if (passwordInput.length < 8) {
      setMessage("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    setBusy(true);

    const accessInfo = await fetchApprovedAccess(email);
    if (!accessInfo?.approved) {
      setBusy(false);
      setMessage("Ese email no está aprobado para Tournament Ops todavía.");
      return;
    }

    if (accessInfo.authUserExists) {
      setBusy(false);
      setMessage(
        accessInfo.emailConfirmed
          ? "Esa cuenta ya existe. Entra por Ya tengo acceso o usa Recuperar contraseña si no recuerdas tu clave."
          : "Esa cuenta ya fue creada pero parece pendiente de confirmar. Revisa tu correo anterior o usa Magic Link si te lo indicaron."
      );
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password: passwordInput,
      options: {
        emailRedirectTo: `${window.location.origin}/tournament-ops/gamergy`,
      },
    });

    setBusy(false);

    if (error) {
      const normalized = String(error.message || "").toLowerCase();
      if (
        normalized.includes("already registered")
        || normalized.includes("already been registered")
        || normalized.includes("user already registered")
      ) {
        setMessage("Esa cuenta ya existe. Usa Contraseña o Recuperar contraseña.");
        return;
      }

      setMessage(formatAuthMessage("No se pudo crear el acceso inicial", error));
      return;
    }

    if (data?.session) {
      setMessage("Acceso creado. Ya puedes entrar a Tournament Ops.");
      return;
    }

    setMessage("Acceso inicial creado. Revisa tu correo por si Supabase te pide confirmar el email antes de entrar.");
  }

  async function sendMagicLink(e) {
    e.preventDefault();
    setMessage("");

    if (!supabase) return;

    const email = emailInput.trim().toLowerCase();
    if (!email) {
      setMessage("Escribe un email válido.");
      return;
    }

    setBusy(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/tournament-ops/gamergy`,
      },
    });
    setBusy(false);

    if (error) {
      setMessage(formatAuthMessage("No se pudo enviar magic link", error));
      return;
    }

    setMessage("Te envié un magic link. Abre ese correo y vuelve a Tournament Ops.");
  }

  async function sendResetPassword() {
    setMessage("");
    if (!supabase) return;

    const email = emailInput.trim().toLowerCase();
    if (!email) {
      setMessage("Escribe tu email para recuperar contraseña.");
      return;
    }

    setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/tournament-ops/gamergy`,
    });
    setBusy(false);

    if (error) {
      setMessage(formatAuthMessage("No se pudo enviar recuperación", error));
      return;
    }

    setMessage("Te envié el correo de recuperación de contraseña.");
  }

  async function logout() {
    if (isLocalDevTournamentBypass) {
      setMessage("Bypass local activo. Cierra la pestaña o sal de localhost para volver al acceso normal.");
      return;
    }

    if (!supabase) return;
    await supabase.auth.signOut();
    setAccess(null);
    setSession(null);
  }

  if (loading) {
    return (
      <div className={`${OPS_SHELL_CLASS} flex items-center justify-center px-4 py-6 sm:p-6`}>
        <div className={`${OPS_PANEL_CLASS} px-6 py-4 text-sm font-medium tracking-[0.08em] text-black/70 uppercase`}>
          Cargando Tournament Ops...
        </div>
      </div>
    );
  }

  if (!session?.user) {
    return (
      <div className={`${OPS_SHELL_CLASS} flex items-center justify-center px-4 py-6 sm:p-6`}>
        <div className={`${OPS_PANEL_CLASS} relative w-full max-w-5xl overflow-hidden p-4 sm:p-6 lg:p-8`}>
          <div className="pointer-events-none absolute -left-16 top-0 h-48 w-48 rounded-full bg-violet-400/35 blur-3xl" />
          <div className="pointer-events-none absolute right-0 top-0 h-56 w-56 rounded-full bg-rose-300/35 blur-3xl" />
          <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.78fr)]">
            <section className="rounded-[28px] border border-black/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.76),rgba(255,255,255,0.58))] p-6 sm:p-8">
              <div className="inline-flex rounded-full border border-black/10 bg-white/80 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-black/65">
                Tournament Ops
              </div>
              <h1 className="mt-5 max-w-3xl text-4xl font-black tracking-[-0.04em] text-[#111111] sm:text-5xl lg:text-6xl">
                Run the whole tournament floor without the mess.
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-black/65 sm:text-base">
                Acceso privado para staff de PREL. Aquí viven los registros, check-ins, brackets y decisiones rápidas de operación para GAMERGY.
              </p>
              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {[
                  ["01", "Roster control", "Confirma equipos, acomoda free agents y limpia dudas rápido."],
                  ["02", "Check-in live", "Marca presencia por juego sin perder de vista los cupos."],
                  ["03", "Bracket flow", "Salta de registros a cruces sin cambiar de entorno."],
                ].map(([index, title, copy]) => (
                  <div key={index} className="rounded-[26px] border border-black/10 bg-white/78 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-black/45">{index}</div>
                    <div className="mt-3 text-base font-bold text-[#111111]">{title}</div>
                    <p className="mt-2 text-sm leading-6 text-black/58">{copy}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="relative rounded-[28px] border border-black/10 bg-white/92 p-5 shadow-[0_18px_50px_rgba(17,17,17,0.07)] sm:p-6">
          <img
            src="/tournament-ops-assets/prel-logo-v1.png"
            alt="Puerto Rico Esports League"
            className="h-12 w-auto object-contain sm:h-14"
            loading="eager"
            decoding="async"
          />
          <h2 className="mt-5 text-3xl font-black tracking-[-0.04em] text-[#111111]">Tournament Ops Access</h2>
          <p className="mt-2 text-sm leading-6 text-black/62">
            Entrada para staff de torneo. Si es tu primera vez, crea tu acceso primero y luego entra con tu contraseña.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <button
              onClick={() => setMode("signup")}
              className={`${OPS_TAB_BUTTON_CLASS} ${mode === "signup" ? "border-violet-300 bg-[linear-gradient(135deg,#ede9fe,#ffe4e6)] text-[#3b0764]" : "border-black/10 bg-white text-black/65 hover:border-black/25"}`}
            >
              Primer acceso
            </button>
            <button
              onClick={() => setMode("password")}
              className={`${OPS_TAB_BUTTON_CLASS} ${mode === "password" ? "border-violet-300 bg-[linear-gradient(135deg,#ede9fe,#ffe4e6)] text-[#3b0764]" : "border-black/10 bg-white text-black/65 hover:border-black/25"}`}
            >
              Ya tengo acceso
            </button>
            <button
              onClick={() => setMode("magic")}
              className={`${OPS_TAB_BUTTON_CLASS} ${mode === "magic" ? "border-violet-300 bg-[linear-gradient(135deg,#ede9fe,#ffe4e6)] text-[#3b0764]" : "border-black/10 bg-white text-black/65 hover:border-black/25"}`}
            >
              Magic Link
            </button>
          </div>

          {mode === "signup" ? (
            <form onSubmit={createPasswordAccess} className="mt-4 space-y-3">
              <div className="rounded-[24px] border border-black/10 bg-[#f8fbe8] px-4 py-3 text-sm leading-6 text-black/70">
                Usa esta opción si todavía no has creado tu acceso para Tournament Ops.
              </div>
              <input
                type="email"
                required
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="tu-email@empresa.com"
                className={OPS_INPUT_CLASS}
              />
              <input
                type="password"
                required
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Crea una contraseña"
                className={OPS_INPUT_CLASS}
              />
              <button disabled={busy} className={OPS_PRIMARY_BUTTON_CLASS}>
                Crear acceso
              </button>
              <p className="text-xs leading-6 text-black/50">
                Solo funciona para correos ya aprobados dentro del equipo de Tournament Ops.
              </p>
            </form>
          ) : mode === "password" ? (
            <form onSubmit={loginWithPassword} className="mt-4 space-y-3">
              <div className="rounded-[24px] border border-black/10 bg-black/[0.025] px-4 py-3 text-sm leading-6 text-black/70">
                Si ya creaste tu acceso antes, entra aquí con tu email y contraseña.
              </div>
              <input
                type="email"
                required
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="tu-email@empresa.com"
                className={OPS_INPUT_CLASS}
              />
              <input
                type="password"
                required
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Tu contraseña"
                className={OPS_INPUT_CLASS}
              />
              <button disabled={busy} className={OPS_PRIMARY_BUTTON_CLASS}>
                Entrar
              </button>
              <button type="button" onClick={sendResetPassword} disabled={busy} className={`w-full ${OPS_SECONDARY_BUTTON_CLASS}`}>
                Recuperar contraseña
              </button>
              <p className="text-xs leading-6 text-black/50">
                Si es tu primera vez y todavía no tienes contraseña, vuelve a <strong>Primer acceso</strong>.
              </p>
            </form>
          ) : (
            <form onSubmit={sendMagicLink} className="mt-4 space-y-3">
              <div className="rounded-[24px] border border-black/10 bg-cyan-50/70 px-4 py-3 text-sm leading-6 text-black/70">
                Usa esto solo si te indicaron entrar por correo. Si es tu primera vez, te conviene más <strong>Primer acceso</strong>.
              </div>
              <input
                type="email"
                required
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="tu-email@empresa.com"
                className={OPS_INPUT_CLASS}
              />
              <button disabled={busy} className={OPS_PRIMARY_BUTTON_CLASS}>
                Enviar Magic Link
              </button>
            </form>
          )}

          {message ? <p className="mt-4 rounded-[22px] border border-black/10 bg-black/[0.03] px-4 py-3 text-sm leading-6 text-black/68">{message}</p> : null}
          <TournamentOpsFooter />
            </section>
          </div>
        </div>
      </div>
    );
  }

  if (!access?.role) {
    return (
      <div className={`${OPS_SHELL_CLASS} flex items-center justify-center px-4 py-6 sm:p-6`}>
        <div className={`${OPS_PANEL_CLASS} w-full max-w-lg p-5 text-[#111111] sm:p-6`}>
          <img
            src="/tournament-ops-assets/prel-logo-v1.png"
            alt="Puerto Rico Esports League"
            className="h-12 w-auto object-contain sm:h-14"
            loading="eager"
            decoding="async"
          />
          <h1 className="mt-5 text-3xl font-black tracking-[-0.04em]">Acceso restringido</h1>
          <p className="mt-3 leading-7 text-black/72">
            Tu cuenta (<strong>{session.user.email}</strong>) no está habilitada para <code>tournament-ops</code>.
          </p>
          <p className="mt-2 text-sm leading-6 text-black/58">Agrégala en <code>tournament_staff_users</code> para staff de torneo, o habilítala como admin si solo necesitas probar el entorno.</p>
          <button onClick={logout} className={`mt-5 ${OPS_SECONDARY_BUTTON_CLASS}`}>Cerrar sesión</button>
          <TournamentOpsFooter />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-2 border-b border-black/10 bg-white/82 px-4 py-3 text-xs text-black/58 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
        <span className="break-all sm:break-normal">
          Tournament Ops: <strong>{session.user.email}</strong> · Rol: <strong>{access.role}</strong>
        </span>
        <button onClick={logout} className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-black/72 transition hover:border-black/20 hover:bg-black/[0.03]">Cerrar sesión</button>
      </div>
      {children}
    </div>
  );
}
